/**
 * Event-level tableBookingAutoPriceHike — exact backend shape.
 * { enabled, bookingsPerTier, hikeType: PERCENT|FIXED, hikeValue, scope: ALL|SECTION, areaIds[] }
 */

import {
  HIKE_TYPE,
  applyHikeToUnitPrice,
} from "./tableBookingPriceHike";

export const AUTO_HIKE_SCOPE = {
  ALL: "ALL",
  SECTION: "SECTION",
};

export function emptyTableBookingAutoPriceHike() {
  return {
    enabled: false,
    bookingsPerTier: 10,
    hikeType: HIKE_TYPE.PERCENT,
    hikeValue: 0,
    scope: AUTO_HIKE_SCOPE.ALL,
    areaIds: [],
  };
}

export function pickTableBookingAutoPriceHike(event) {
  const nested =
    event?.tableBookingAutoPriceHike ??
    event?.tableBooking?.autoPriceHike ??
    event?.tableBooking?.tableBookingAutoPriceHike ??
    null;
  if (!nested || typeof nested !== "object") {
    return emptyTableBookingAutoPriceHike();
  }
  const hikeType =
    String(nested.hikeType ?? nested.hike_type ?? HIKE_TYPE.PERCENT).toUpperCase() ===
    HIKE_TYPE.FIXED
      ? HIKE_TYPE.FIXED
      : HIKE_TYPE.PERCENT;
  const scopeRaw = String(nested.scope ?? AUTO_HIKE_SCOPE.ALL).toUpperCase();
  const scope =
    scopeRaw === AUTO_HIKE_SCOPE.SECTION
      ? AUTO_HIKE_SCOPE.SECTION
      : AUTO_HIKE_SCOPE.ALL;
  const areaIds = Array.isArray(nested.areaIds)
    ? nested.areaIds.map((id) => String(id).trim()).filter(Boolean)
    : [];
  const hikeValue = Number(nested.hikeValue ?? nested.hike_value ?? 0);
  const perTier = parseInt(
    String(nested.bookingsPerTier ?? nested.bookings_per_tier ?? 10),
    10
  );
  return {
    enabled: Boolean(nested.enabled),
    bookingsPerTier: Number.isFinite(perTier) && perTier >= 1 ? perTier : 10,
    hikeType,
    hikeValue: Number.isFinite(hikeValue) ? hikeValue : 0,
    scope,
    areaIds,
  };
}

export function pickAutoPriceHikeStatus(event) {
  const status =
    event?.tableBooking?.autoPriceHikeStatus ??
    event?.autoPriceHikeStatus ??
    null;
  return status && typeof status === "object" ? status : null;
}

/** Stack hike on current price each tier (e.g. 400 × 200% → 1200 → 3600). */
export function applyAutoTierToUnitPrice(basePrice, autoHike, tierIndex) {
  let price = Number(basePrice) || 0;
  const n = Math.max(0, Math.floor(Number(tierIndex) || 0));
  if (!autoHike?.enabled || n <= 0) return Math.max(0, price);
  const value = Number(autoHike.hikeValue) || 0;
  for (let i = 0; i < n; i += 1) {
    if (autoHike.hikeType === HIKE_TYPE.FIXED) {
      price = Math.max(0, price + value);
    } else {
      price = Math.max(0, Math.round(price * (1 + value / 100)));
    }
  }
  return price;
}

export function buildAutoHikePreviewLadder(basePrice, autoHike, steps = 4) {
  const n = Math.max(1, parseInt(String(autoHike?.bookingsPerTier), 10) || 1);
  const enabled = { ...autoHike, enabled: true };
  return Array.from({ length: steps }, (_, tier) => ({
    tierIndex: tier,
    fromCount: tier * n,
    toCount: (tier + 1) * n - 1,
    price: applyAutoTierToUnitPrice(basePrice, enabled, tier),
  }));
}

export function sectionMatchesAutoScope(areaId, autoHike) {
  if (!autoHike?.enabled) return false;
  if (autoHike.scope === AUTO_HIKE_SCOPE.ALL) return true;
  return (autoHike.areaIds || []).includes(String(areaId ?? "").trim());
}

export function resolveAutoStatusForArea(status, areaId) {
  if (!status) return null;
  if (Array.isArray(status.sections)) {
    const hit = status.sections.find(
      (s) => String(s?.areaId ?? s?.area_id ?? "") === String(areaId ?? "")
    );
    return hit || null;
  }
  return status;
}

export function validateTableBookingAutoPriceHike(autoHike, t) {
  if (!autoHike?.enabled) return null;
  const perTier = parseInt(String(autoHike.bookingsPerTier), 10);
  if (!Number.isFinite(perTier) || perTier < 1) {
    return t("EVENT_AUTO_HIKE_ERR_N");
  }
  const value = Number(autoHike.hikeValue);
  if (!Number.isFinite(value) || value < 0) {
    return t("EVENT_HIKE_ERR_VALUE");
  }
  if (
    autoHike.scope === AUTO_HIKE_SCOPE.SECTION &&
    !(autoHike.areaIds || []).length
  ) {
    return t("EVENT_HIKE_ERR_SECTIONS");
  }
  return null;
}

export function buildTableBookingAutoPriceHikePayload(autoHike) {
  if (!autoHike?.enabled) {
    return { tableBookingAutoPriceHike: { enabled: false } };
  }
  const payload = {
    enabled: true,
    bookingsPerTier: Math.max(
      1,
      parseInt(String(autoHike.bookingsPerTier), 10) || 1
    ),
    hikeType:
      autoHike.hikeType === HIKE_TYPE.FIXED ? HIKE_TYPE.FIXED : HIKE_TYPE.PERCENT,
    hikeValue: Number(autoHike.hikeValue) || 0,
    scope:
      autoHike.scope === AUTO_HIKE_SCOPE.SECTION
        ? AUTO_HIKE_SCOPE.SECTION
        : AUTO_HIKE_SCOPE.ALL,
    areaIds: [],
  };
  if (payload.scope === AUTO_HIKE_SCOPE.SECTION) {
    payload.areaIds = [...(autoHike.areaIds || [])];
  }
  return { tableBookingAutoPriceHike: payload };
}

export function priceAfterManualThenAuto(layoutBase, manualHike, autoHike, tierIndex) {
  const afterManual = applyHikeToUnitPrice(layoutBase, manualHike);
  return applyAutoTierToUnitPrice(afterManual, autoHike, tierIndex);
}

export { HIKE_TYPE };
